import type { components } from "../generated/openapi";
import { API_PREFIX } from "../config/app";
import { getApiBaseUrl } from "../config/env";
import { isRecord, readRecord, readString } from "../lib/object";

export type Schema<Name extends keyof components["schemas"]> = components["schemas"][Name];

export type ApiMeta = Partial<Schema<"ResponseMeta">> & {
  pagination?: Schema<"PaginationMeta">;
};

export type ApiFailure = {
  code: string;
  message: string;
  details?: Schema<"APIError">["details"];
};

export class ApiRequestError extends Error {
  code: string;
  status: number;
  requestId?: string;
  details?: Schema<"APIError">["details"];

  constructor(input: { code: string; message?: string; status: number; requestId?: string; details?: Schema<"APIError">["details"] }) {
    super(input.message ?? input.code);
    this.name = "ApiRequestError";
    this.code = input.code;
    this.status = input.status;
    this.requestId = input.requestId;
    this.details = input.details;
  }
}

type ApiRequestOptions<TBody> = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Record<string, string | number | boolean | undefined>;
  body?: TBody;
  signal?: AbortSignal;
  idempotencyKey?: string;
  headers?: Record<string, string>;
};

type ApiBlobOptions = {
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export type ApiBlobResponse = {
  blob: Blob;
  contentDisposition: string;
  contentType: string;
};

export type ApiSuccessEnvelope = {
  success: true;
  meta: Schema<"ResponseMeta"> | Schema<"PaginatedResponseMeta">;
  data?: unknown;
};

export type EnvelopeData<TEnvelope> = TEnvelope extends { data: infer TData } ? TData : undefined;

type ApiEvent = "unauthorized" | "forbidden";
type ApiEventHandler = () => void;
const eventHandlers = new Map<ApiEvent, Set<ApiEventHandler>>();
let csrfTokenOverride = "";

export function onApiEvent(event: ApiEvent, handler: ApiEventHandler): () => void {
  const handlers = eventHandlers.get(event) ?? new Set<ApiEventHandler>();
  handlers.add(handler);
  eventHandlers.set(event, handlers);
  return () => handlers.delete(handler);
}

function emitApiEvent(event: ApiEvent): void {
  eventHandlers.get(event)?.forEach((handler) => handler());
}

export async function apiEnvelope<TEnvelope extends ApiSuccessEnvelope = Schema<"SuccessEnvelope">, TBody = undefined>(
  path: string,
  options: ApiRequestOptions<TBody> = {}
): Promise<TEnvelope> {
  const method = options.method ?? "GET";
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  if (options.idempotencyKey) {
    headers.set("Idempotency-Key", options.idempotencyKey);
  }

  if (method !== "GET" && !headers.has("X-CSRF-Token")) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers.set("X-CSRF-Token", csrf);
    }
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    credentials: "include",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal
  });

  const parsed = await parseJson(response);
  const meta = extractMeta(parsed);

  if (!response.ok || isFailureEnvelope(parsed)) {
    const failure = extractFailure(parsed);
    const error = new ApiRequestError({
      code: failure.code,
      message: failure.message,
      status: response.status,
      requestId: meta.request_id,
      details: failure.details
    });
    if (response.status === 401 || failure.code === "AUTH_UNAUTHORIZED") {
      emitApiEvent("unauthorized");
    }
    if (response.status === 403 || failure.code === "AUTH_FORBIDDEN") {
      emitApiEvent("forbidden");
    }
    throw error;
  }

  if (isSuccessEnvelope(parsed)) {
    return parsed as TEnvelope;
  }

  throw new ApiRequestError({
    code: "INTERNAL_ERROR",
    message: "INTERNAL_ERROR",
    status: response.status,
    requestId: meta.request_id
  });
}

export async function apiData<TEnvelope extends ApiSuccessEnvelope = Schema<"SuccessEnvelope">, TBody = undefined>(
  path: string,
  options: ApiRequestOptions<TBody> = {}
): Promise<EnvelopeData<TEnvelope>> {
  const envelope = await apiEnvelope<TEnvelope, TBody>(path, options);
  return envelope.data as EnvelopeData<TEnvelope>;
}

export async function apiRequest<TEnvelope extends ApiSuccessEnvelope = Schema<"SuccessEnvelope">, TBody = undefined>(
  path: string,
  options: ApiRequestOptions<TBody> = {}
): Promise<EnvelopeData<TEnvelope>> {
  return apiData<TEnvelope, TBody>(path, options);
}

export async function apiBlob(path: string, options: ApiBlobOptions = {}): Promise<ApiBlobResponse> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "*/*");

  const response = await fetch(buildUrl(path, options.query), {
    method: "GET",
    credentials: "include",
    headers,
    signal: options.signal
  });

  if (!response.ok) {
    const parsed = await parseJson(response);
    const meta = extractMeta(parsed);
    const failure = extractFailure(parsed);
    const error = new ApiRequestError({
      code: failure.code,
      message: failure.message,
      status: response.status,
      requestId: meta.request_id,
      details: failure.details
    });
    if (response.status === 401 || failure.code === "AUTH_UNAUTHORIZED") {
      emitApiEvent("unauthorized");
    }
    if (response.status === 403 || failure.code === "AUTH_FORBIDDEN") {
      emitApiEvent("forbidden");
    }
    throw error;
  }

  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get("Content-Disposition") ?? "",
    contentType: response.headers.get("Content-Type") ?? ""
  };
}

export function apiPath(path: string): string {
  return path.startsWith(API_PREFIX) ? path : `${API_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
}

export function resolveApiUrl(path: string): string {
  return buildUrl(path);
}

export function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)watertracker_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : csrfTokenOverride;
}

export function setCsrfToken(token: string): void {
  csrfTokenOverride = token;
}

function buildUrl(path: string, query?: Record<string, string | number | boolean | undefined>): string {
  const baseUrl = getApiBaseUrl();
  const normalizedPath = apiPath(path);
  const url = `${baseUrl}${normalizedPath}`;
  const search = new URLSearchParams();
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  });
  const suffix = search.toString();
  return suffix ? `${url}?${suffix}` : url;
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isFailureEnvelope(value: unknown): boolean {
  return isRecord(value) && value.success === false;
}

function isSuccessEnvelope(value: unknown): value is ApiSuccessEnvelope {
  return isRecord(value) && value.success === true && isRecord(value.meta);
}

function extractMeta(value: unknown): ApiMeta {
  if (!isRecord(value)) {
    return {};
  }
  const meta = readRecord(value, "meta");
  if (!meta) {
    return {};
  }
  const pagination = readRecord(meta, "pagination");
  return {
    request_id: readString(meta, "request_id"),
    pagination
  };
}

function extractFailure(value: unknown): ApiFailure {
  if (!isRecord(value)) {
    return { code: "INTERNAL_ERROR", message: "INTERNAL_ERROR" };
  }
  const error = readRecord(value, "error");
  if (!error) {
    return { code: "INTERNAL_ERROR", message: `HTTP_${readString(value, "status") ?? "ERROR"}` };
  }
  return {
    code: readString(error, "code") ?? "INTERNAL_ERROR",
    message: readString(error, "message") ?? readString(error, "code") ?? "INTERNAL_ERROR",
    details: isRecord(error.details) ? error.details : undefined
  };
}
